import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { AYS_VIRTUAL_CLINIC_SELECT_OPTIONS_DEFAULT } from '../virtual-clinic.const';
import { AysVirtualClinicStructuralSelect } from './structural-select';
import {
  AysVirtualClinic,
  AysVirtualClinicSpecialtyDepartment,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import { AysVirtualClinicStructuralSelectYearComponent } from './year/year.component';
import { AysVirtualClinicSpecialtyDepartmentsInlineMultipleSelectComponent } from '../specialty-department/inline-multiple/inline-multiple.component';
import { AysVirtualClinicSelectInlineComponent } from '../inline/inline.component';
import { BdResourcesSelectInlineConfig } from '@bindoc-projects/resource-lib/data';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'ays-virtual-clinic-structural-select',
  template: `
    <div class="flex flex-row gap-6">
      <div class="flex-1">
        <ays-virtual-clinic-select-inline
          [options]="virtualClinicSelectOptions"
          (virtualClinicSelect)="onVirtualClinicChange($event)"
        >
        </ays-virtual-clinic-select-inline>
      </div>

      @if (withSpecialtyDepartment()) {
        <div class="flex-1">
          @if (currentSelection().virtualClinic) {
            <ays-virtual-clinic-specialty-departments-inline-multiple-select
              [virtualClinicId]="currentSelection().virtualClinic.id"
              (specialtyDepartmentSelect)="onSpecialtyDepartmentChange($event)"
            >
            </ays-virtual-clinic-specialty-departments-inline-multiple-select>
          } @else {
            <p-skeleton height="100%"></p-skeleton>
          }
        </div>
      }

      <div class="flex-1">
        @if (currentSelection().virtualClinic) {
          <ays-virtual-clinic-structural-select-year
            [virtualClinic]="currentSelection().virtualClinic"
            (yearSelect)="onYearChange($event)"
          >
          </ays-virtual-clinic-structural-select-year>
        } @else {
          <p-skeleton height="100%"></p-skeleton>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AysVirtualClinicSelectInlineComponent,
    AysVirtualClinicSpecialtyDepartmentsInlineMultipleSelectComponent,
    AysVirtualClinicStructuralSelectYearComponent,
    SkeletonModule,
  ],
})
export class AysVirtualClinicStructuralSelectComponent {
  readonly selection = input<AysVirtualClinicStructuralSelect | undefined>(
    undefined,
  );
  readonly withSpecialtyDepartment = input<boolean>(true);

  readonly selectionChange = output<AysVirtualClinicStructuralSelect>();

  readonly currentSelection = signal<AysVirtualClinicStructuralSelect>({});

  public readonly virtualClinicSelectOptions: BdResourcesSelectInlineConfig<AysVirtualClinic> =
    {
      ...AYS_VIRTUAL_CLINIC_SELECT_OPTIONS_DEFAULT,
      selectOnInit: 'none',
    };

  constructor() {
    effect(() => {
      const sel = this.selection();
      if (sel) {
        this.currentSelection.set(sel);
      }
    });
  }

  public onVirtualClinicChange(virtualClinic: AysVirtualClinic) {
    this.currentSelection.set({
      virtualClinic,
      specialtyDepartments: null,
      year: null,
    });
    this._onSelectionChange();
  }

  public onSpecialtyDepartmentChange(
    specialtyDepartments: AysVirtualClinicSpecialtyDepartment[],
  ) {
    this.currentSelection.update((s) => ({ ...s, specialtyDepartments }));
    this._onSelectionChange();
  }

  public onYearChange(year: number) {
    this.currentSelection.update((s) => ({ ...s, year }));
    this._onSelectionChange();
  }

  private _onSelectionChange() {
    if (this._selectionIsValid()) {
      this.selectionChange.emit(this.currentSelection());
    }
  }

  private _selectionIsValid(): boolean {
    const sel = this.currentSelection();
    return this.withSpecialtyDepartment()
      ? !!sel.virtualClinic && !!sel.specialtyDepartments && !!sel.year
      : !!sel.virtualClinic && !!sel.year;
  }
}
